import { useQueryClient } from 'react-query';
import { useState } from 'react';
import { Brief, getNewBrief } from '@fiverr-private/ai_brief';
import { stats } from '@fiverr-private/obs';
import { RolloutsList, useBriefCreationContext } from '../context/BriefCreationContext';
import { METRICS_PREFIX, Step } from '../constants';
import { BriefClassificationResponse, generateBrief, LeafClassification } from '../api';
import { reportTimeMetric } from '../monitoring';
import { useCustomSections } from '../components';
import { logger } from '../utils/logger';
import { InvalidIntent, invalidIntents } from '../i18n';
import {
    generateBriefQueryKey,
    PARALLEL_GRACE_PERIOD_MS,
    pollGenerationQueryKey,
    UNABLE_TO_PRODUCE_A_BRIEF,
} from './constants';
import { QUERY_RETRY_DELAY_MS, QUERY_TIMEOUT_MS, useExtractAdditionalData } from './classification';

const extractAdditionalDataRequestType = 'extract_additional_data';
const generateBriefRequestType = 'generate_brief';

export const useExtractAndGenerateBrief = () => {
    const { setStep, contextId, source, userInput, setBrief, setBriefClassification } = useBriefCreationContext();
    const { extractAdditionalData } = useExtractAdditionalData();
    const { addCustomSections } = useCustomSections();

    const handleGenerationError = (error?: Error) => {
        logger.error(new Error('Failed while generating brief'), { userInput, cause: error });
        setStep((prevStep: Step) => {
            if (prevStep === Step.Loading) {
                return Step.Error;
            }
            return prevStep;
        });
        stats.count(`${METRICS_PREFIX}.${generateBriefRequestType}`, `error`);
    };

    const handleInvalidIntent = (invalidIntent: InvalidIntent) => {
        logger.warn(new Error('Invalid intent'), { intentClassification: invalidIntent, userInput });

        setStep(Step.Error);

        stats.count(`${METRICS_PREFIX}.classify_user_input`, `invalid_intent`);
    };

    const handleRephraseWarning = (error?: Error) => {
        logger.warn(new Error('Insufficient information'), { cause: error });
        setStep((prevStep: Step) => {
            if (prevStep === Step.Loading) {
                return Step.Rephrase;
            }
            return prevStep;
        });
        stats.count(`${METRICS_PREFIX}.${generateBriefRequestType}`, `rephrase`);
    };

    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const handleGenerationResult = (generatedBrief: Brief) => {
        if (!generatedBrief) {
            handleGenerationError(new Error('Failed to generate brief'));
            return;
        }

        setStep((prevStep: Step) => {
            if (prevStep === Step.Loading) {
                return Step.Edit;
            }
            return prevStep;
        });
        return generatedBrief;
    };

    const handleGenerate = async ({
        userInput,
        leafClassification,
        generationTimeStart,
        rollouts = {},
    }: {
        userInput: string;
        leafClassification: LeafClassification;
        generationTimeStart: number;
        rollouts?: RolloutsList;
    }): Promise<ReturnType<typeof getNewBrief> | undefined> => {
        const briefType = rollouts?.inMustangsPersonBrief ? 'dynamic_matching_person' : 'dynamic_matching';

        const subcategoryId = leafClassification.sub_category_id;
        setStep(Step.Loading);
        setIsLoading(true);

        try {
            const { data: generationPollingId } = await queryClient.fetchQuery({
                queryKey: [generateBriefQueryKey, userInput],
                queryFn: () => generateBrief({ userInput, subcategoryId, contextId, source, briefType }),
                cacheTime: 0,
                retry: 2,
            });

            if (!generationPollingId) {
                handleGenerationError();
                return;
            }

            if (!userInput) {
                return;
            }

            const generatedBrief = await queryClient.fetchQuery<Brief>({
                queryKey: [pollGenerationQueryKey, userInput],
                queryFn: () => getNewBrief(generationPollingId, source || '', 'dynamic_matching'),
                retry: QUERY_TIMEOUT_MS / QUERY_RETRY_DELAY_MS,
                retryDelay: QUERY_RETRY_DELAY_MS,
            });

            if (generatedBrief.error === UNABLE_TO_PRODUCE_A_BRIEF) {
                handleRephraseWarning(generatedBrief?.error ? new Error(generatedBrief.error) : undefined);
                return;
            }

            if (generatedBrief.error && invalidIntents.includes(generatedBrief.error as InvalidIntent)) {
                handleInvalidIntent(generatedBrief.error as InvalidIntent);
                return;
            }

            if (!generatedBrief || generatedBrief.error) {
                handleGenerationError(generatedBrief?.error ? new Error(generatedBrief.error) : undefined);
                return;
            }

            stats.count(`${METRICS_PREFIX}.${generateBriefRequestType}`, `success`);
            reportTimeMetric({
                metricName: 'extract_and_generate_brief',
                time: Date.now() - generationTimeStart,
            });
            return handleGenerationResult(generatedBrief);
        } catch (error) {
            handleGenerationError(error as Error);
        } finally {
            setIsLoading(false);
        }
    };

    const extractAndGenerateBrief = async ({
        extractedData,
        leafClassification,
        userInput,
        classification,
        abortController,
        rollouts = {},
    }: {
        extractedData?: BriefClassificationResponse;
        leafClassification: LeafClassification;
        userInput: string;
        classification?: BriefClassificationResponse;
        abortController: AbortController;
        rollouts?: RolloutsList;
    }) => {
        const generationTimeStart = Date.now();
        let abortHandle: NodeJS.Timeout;
        let resolveAbort;

        if (!extractedData) {
            extractAdditionalData({
                leafClassification,
                contextId,
                userInput,
                abortController,
                requestType: extractAdditionalDataRequestType,
            })
                .then((additionalData) => {
                    extractedData = additionalData;
                    clearTimeout(abortHandle);
                    resolveAbort?.();
                })
                .catch();
        }

        const generatedBrief = await handleGenerate({ userInput, leafClassification, generationTimeStart, rollouts });
        if (!extractedData) {
            setStep((prevStep: Step) => {
                if (prevStep === Step.Error) {
                    return Step.Error;
                }
                return Step.Loading;
            });
            await new Promise<void>((resolve) => {
                resolveAbort = resolve;
                abortHandle = setTimeout(() => {
                    if (!extractedData) {
                        logger.warn(new Error('AI Brief Generation finished before extract additional data'), {
                            userInput,
                        });
                        abortController.abort();
                        extractedData = classification;
                    }
                    resolve();
                }, PARALLEL_GRACE_PERIOD_MS);
            });
        }

        if (!generatedBrief) {
            return;
        }

        if (!extractedData) {
            logger.error(new Error('Failed to extract additional data. This should not happen'), { userInput });
            return;
        }

        const brief = addCustomSections({ brief: generatedBrief, additionalData: extractedData });
        setBriefClassification(extractedData);
        setBrief((prevBrief: Brief | null) =>
            prevBrief ? { ...prevBrief, ...(brief?.sections ? { sections: brief.sections } : {}) } : brief || null
        );
        setStep(Step.Edit);
    };

    return {
        isLoading,
        handleGenerate,
        extractAndGenerateBrief,
    };
};
